import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useData } from './DataContext';

const MapContext = createContext(null);

export const useMap = () => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMap must be used within a MapProvider');
  }
  return context;
};

export const MapProvider = ({ children }) => {
  const { clientConfig } = useData();
  const mapSettings = clientConfig.map || {};

  // Compare mode state
  const [isCompareMode, setIsCompareMode] = useState(false);

  // Map instances registry
  const mapsRef = useRef(new Map());

  // Sync state to prevent infinite loops
  const isSyncingRef = useRef(false);

  // Current view state (shared between maps in compare mode)
  const [viewState, setViewState] = useState({
    center: mapSettings.center || [45.62, 9.0],
    zoom: mapSettings.zoom || 10,
  });

  // Update viewState when client config loads/changes
  useEffect(() => {
    if (mapSettings.center) {
      setViewState({
        center: mapSettings.center,
        zoom: mapSettings.zoom || 10,
      });
    }
  }, [mapSettings.center, mapSettings.zoom]);

  // Register a map instance
  const registerMap = useCallback((viewId, mapInstance) => {
    if (mapInstance) {
      mapsRef.current.set(viewId, mapInstance);
    } else {
      mapsRef.current.delete(viewId);
    }
  }, []);

  // Sync all maps to a specific view state
  const syncMaps = useCallback((sourceViewId, center, zoom) => {
    // Prevent recursive syncing
    if (isSyncingRef.current) return;

    isSyncingRef.current = true;

    // Update shared view state
    setViewState({ center, zoom });

    // Sync all other map instances
    mapsRef.current.forEach((mapInstance, viewId) => {
      if (viewId !== sourceViewId && mapInstance) {
        try {
          mapInstance.setView(center, zoom, { animate: false });
        } catch (error) {
          console.warn(`Failed to sync map ${viewId}:`, error);
        }
      }
    });

    // Reset sync flag after a short delay
    setTimeout(() => {
      isSyncingRef.current = false;
    }, 50);
  }, []);

  // Toggle compare mode
  const toggleCompareMode = useCallback(() => {
    setIsCompareMode((prev) => !prev);
  }, []);

  // Get map instance by view ID
  const getMapInstance = useCallback((viewId) => {
    return mapsRef.current.get(viewId);
  }, []);

  const value = {
    isCompareMode,
    setIsCompareMode,
    toggleCompareMode,
    registerMap,
    syncMaps,
    getMapInstance,
    viewState,
    setViewState,
    isSyncing: isSyncingRef.current,
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};

export default MapContext;
